 import { useState } from "react";
 import { MapPin, Plus, Search, Loader2, X } from "lucide-react";
 import {
   Dialog,
   DialogContent,
   DialogHeader,
   DialogTitle,
   DialogDescription,
 } from "@/components/ui/dialog";
 import { Button } from "@/components/ui/button";
 import { Input } from "@/components/ui/input";
 import { ScrollArea } from "@/components/ui/scroll-area";
 import { useDebounce } from "@/hooks/useDebounce";
 import { useSearchCities } from "@/hooks/useLocationData";
 import { ExhibitionCountry } from "@/hooks/useExhibition";
 import { cn } from "@/lib/utils";
 
 interface CustomCityEntryModalProps {
   isOpen: boolean;
   onClose: () => void;
   country: ExhibitionCountry;
   onSelectCity: (city: { id: string; name: string; isManual: boolean }) => void;
 }
 
 const CustomCityEntryModal: React.FC<CustomCityEntryModalProps> = ({
   isOpen,
   onClose,
   country,
   onSelectCity,
 }) => {
   const [search, setSearch] = useState("");
   const debouncedSearch = useDebounce(search, 300);
   
   const { data: cities = [], isLoading } = useSearchCities(
     country.code,
     null,
     debouncedSearch
   );
   
   const handleSelectFromDb = (city: { id: string; name: string }) => {
     onSelectCity({ ...city, isManual: false });
     setSearch("");
     onClose();
   };
   
   const handleManualEntry = () => {
     if (!search.trim()) return;
     onSelectCity({
       id: `custom-${country.code}-${Date.now()}`,
       name: search.trim(),
       isManual: true,
     });
     setSearch("");
     onClose();
   };
   
   const hasExactMatch = cities.some(
     c => c.name.toLowerCase() === search.toLowerCase().trim()
   );
 
   return (
     <Dialog open={isOpen} onOpenChange={(open) => !open && onClose()}>
       <DialogContent className="sm:max-w-md">
         <DialogHeader>
           <DialogTitle className="flex items-center gap-2">
             <span className="text-2xl">{country.flag}</span>
             Enter a city in {country.name}
           </DialogTitle>
           <DialogDescription>
             Search for a city or type any city name
           </DialogDescription>
         </DialogHeader>
         
         <div className="space-y-4 mt-2">
           {/* Search Input */}
           <div className="relative">
             <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
             <Input
               placeholder="Type city name..."
               value={search}
               onChange={(e) => setSearch(e.target.value)}
               className="pl-9 pr-9"
               autoFocus
             />
             {search && (
               <button
                 onClick={() => setSearch("")}
                 className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground"
               >
                 <X className="h-4 w-4" />
               </button>
             )}
           </div>
           
           {/* Results */}
           <ScrollArea className="max-h-[250px]">
             {isLoading ? (
               <div className="flex items-center justify-center py-8">
                 <Loader2 className="h-5 w-5 animate-spin text-muted-foreground" />
               </div>
             ) : (
               <div className="space-y-1">
                 {/* DB Cities */}
                 {cities.map((city) => (
                   <button
                     key={city.id}
                     onClick={() => handleSelectFromDb(city)}
                     className={cn(
                       "w-full flex items-center gap-2 px-3 py-2 text-sm rounded-lg transition-colors",
                       "hover:bg-accent hover:text-accent-foreground text-left"
                     )}
                   >
                     <MapPin className="h-4 w-4 text-muted-foreground shrink-0" />
                     <span>{city.name}</span>
                   </button>
                 ))}
                 
                 {/* Manual Entry Option */}
                 {search.trim() && !hasExactMatch && (
                   <button
                     onClick={handleManualEntry}
                     className="w-full flex items-center gap-2 px-3 py-2 text-sm rounded-lg transition-colors hover:bg-primary/10 text-primary border-t mt-2 pt-3"
                   >
                     <Plus className="h-4 w-4" />
                     <span>Use "{search.trim()}" as city name</span>
                   </button>
                 )}
                 
                 {/* Empty State */}
                 {cities.length === 0 && !search.trim() && (
                   <div className="py-8 text-center text-sm text-muted-foreground">
                     Start typing to search cities in {country.name}
                   </div>
                 )}
               </div>
             )}
           </ScrollArea>
           
           {/* Footer Actions */}
           <div className="flex gap-2 pt-2 border-t">
             <Button variant="outline" onClick={onClose} className="flex-1">
               Cancel
             </Button>
             <Button
               onClick={handleManualEntry}
               disabled={!search.trim()}
               className="flex-1"
             >
               Use This City
             </Button>
           </div>
         </div>
       </DialogContent>
     </Dialog>
   );
 };
 
 export default CustomCityEntryModal;